// src/pages/FaaliyetlerPage.js
import React, { useEffect, useMemo, useState } from "react";
import GlobalStyles from "@mui/material/GlobalStyles";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField as MuiTextField,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  List,
  Datagrid,
  EditButton,
  TopToolbar,
  CreateButton,
  ExportButton,
  useRecordContext,
  useRefresh,
  useNotify,
  useRedirect,
  EditBase,
  useEditContext,
  useUpdate,
  FunctionField,
  useListContext,
} from "react-admin";

import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ImageIcon from "@mui/icons-material/Image";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import YouTubeIcon from "@mui/icons-material/YouTube";

import ResimUpload from "../ResimUpload";
import { API_BASE } from "../config";

/* ==========================================================================
   TASARIM SABİTLERİ
   ========================================================================== */

const COLORS = {
  navy: "#0b1f3a",
  navySoft: "#15345d",
  blue: "#2563eb",
  blueSoft: "#eff6ff",
  orange: "#f59e0b",
  orangeSoft: "#fff7ed",
  green: "#10b981",
  greenDark: "#047857",
  greenSoft: "#ecfdf5",
  red: "#ef4444",
  text: "#0f172a",
  textSoft: "#64748b",
  page: "#f3f6fb",
  border: "#e2e8f0",
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "#fff",
    transition: "box-shadow .2s ease",

    "&:hover fieldset": {
      borderColor: "#94a3b8",
    },

    "&.Mui-focused": {
      boxShadow: "0 0 0 4px rgba(37,99,235,.09)",
    },
  },

  "& .MuiInputLabel-root": {
    fontWeight: 650,
  },
};

const PLACEHOLDER = "Bu dilde içerik mevcut değildir.";
const ALLOWED = ["tr", "en", "de"];

/* ==========================================================================
   DİL METİNLERİ
   ========================================================================== */

const T = {
  tr: {
    listTitle: "Faaliyetler",
    listSubtitle:
      "Kurumunuzun çalışmalarını ve sosyal medya içeriklerini yönetin",
    totalLabel: "Toplam faaliyet",

    newBtn: "Yeni Faaliyet",
    exportBtn: "Dışa Aktar",

    save: "Kaydet",
    saving: "Kaydediliyor…",
    cancel: "Vazgeç",

    editTitle: "Faaliyet Düzenle",
    editSubtitle: "Faaliyet içeriğini ve yayın ayarlarını güncelleyin",

    createTitle: "Yeni Faaliyet Ekle",
    createSubtitle:
      "Mobil uygulamanızda yayınlanacak yeni faaliyet oluşturun",

    createdOk: "Faaliyet başarıyla oluşturuldu",
    updatedOk: "Güncelleme başarılı",
    createError: "Kayıt başarısız",
    updateError: "Güncelleme sırasında hata oluştu",

    uploadImage: "Görsel Yükle",
    image: "Resim",
    title: "Başlık",
    description: "Açıklama",
    youtube: "YouTube",
    instagram: "Instagram",

    status: "Durum",
    contentLangShort: "İçerik Dili",
    contentLang: "İçerik Dili",

    active: "Aktif",
    passive: "Pasif",

    edit: "Düzenle",
    delete: "Sil",
    operations: "İşlemler",
    confirmDelete: "Silmek istediğinize emin misiniz?",

    formInfo: "Faaliyet Bilgileri",
    mediaInfo: "Görsel ve Yayın Ayarları",

    preview: "Canlı Önizleme",
    previewInfo:
      "Faaliyet kartınızın uygulamadaki görünümünü kontrol edin.",

    loading: "Yükleniyor…",

    emptyTitle: "Faaliyet eklemeye hoş geldiniz",
    emptyDesc:
      "Eklediğiniz faaliyetler mobil uygulamanızda yayınlanacaktır.",
    emptyBtn: "Hemen Faaliyet Ekle",

    langNames: {
      tr: "Türkçe",
      en: "İngilizce",
      de: "Almanca",
    },
  },

  en: {
    listTitle: "Activities",
    listSubtitle:
      "Manage your organization’s work and social media content",
    totalLabel: "Total activities",

    newBtn: "New Activity",
    exportBtn: "Export",

    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",

    editTitle: "Edit Activity",
    editSubtitle:
      "Update activity content and publishing settings",

    createTitle: "Create New Activity",
    createSubtitle:
      "Create a new activity to publish in your mobile application",

    createdOk: "Created successfully",
    updatedOk: "Updated successfully",
    createError: "Save failed",
    updateError: "An error occurred during the update",

    uploadImage: "Upload Image",
    image: "Image",
    title: "Title",
    description: "Description",
    youtube: "YouTube",
    instagram: "Instagram",

    status: "Status",
    contentLangShort: "Language",
    contentLang: "Content Language",

    active: "Active",
    passive: "Inactive",

    edit: "Edit",
    delete: "Delete",
    operations: "Actions",
    confirmDelete: "Are you sure?",

    formInfo: "Activity Information",
    mediaInfo: "Image and Publishing Settings",

    preview: "Live Preview",
    previewInfo:
      "Check how your activity card appears in the application.",

    loading: "Loading…",

    emptyTitle: "Welcome to adding activities",
    emptyDesc:
      "Items added here will appear in your mobile application.",
    emptyBtn: "Add Activity",

    langNames: {
      tr: "Turkish",
      en: "English",
      de: "German",
    },
  },

  de: {
    listTitle: "Aktivitäten",
    listSubtitle:
      "Verwalten Sie Arbeiten und Social-Media-Inhalte Ihrer Organisation",
    totalLabel: "Aktivitäten insgesamt",

    newBtn: "Neue Aktivität",
    exportBtn: "Exportieren",

    save: "Speichern",
    saving: "Wird gespeichert…",
    cancel: "Abbrechen",

    editTitle: "Aktivität bearbeiten",
    editSubtitle:
      "Inhalt und Veröffentlichungseinstellungen aktualisieren",

    createTitle: "Neue Aktivität erstellen",
    createSubtitle:
      "Erstellen Sie eine neue Aktivität für Ihre mobile Anwendung",

    createdOk: "Erfolgreich erstellt",
    updatedOk: "Erfolgreich aktualisiert",
    createError: "Speichern fehlgeschlagen",
    updateError:
      "Bei der Aktualisierung ist ein Fehler aufgetreten",

    uploadImage: "Bild hochladen",
    image: "Bild",
    title: "Titel",
    description: "Beschreibung",
    youtube: "YouTube",
    instagram: "Instagram",

    status: "Status",
    contentLangShort: "Sprache",
    contentLang: "Inhaltssprache",

    active: "Aktiv",
    passive: "Inaktiv",

    edit: "Bearbeiten",
    delete: "Löschen",
    operations: "Aktionen",
    confirmDelete: "Sind Sie sicher?",

    formInfo: "Aktivitätsinformationen",
    mediaInfo: "Bild- und Veröffentlichungseinstellungen",

    preview: "Live-Vorschau",
    previewInfo:
      "Prüfen Sie die Darstellung Ihrer Aktivität in der App.",

    loading: "Wird geladen…",

    emptyTitle: "Willkommen zum Hinzufügen von Aktivitäten",
    emptyDesc:
      "Elemente erscheinen im Aktivitätenbereich der App.",
    emptyBtn: "Aktivität hinzufügen",

    langNames: {
      tr: "Türkisch",
      en: "Englisch",
      de: "Deutsch",
    },
  },
};

/* ==========================================================================
   DİL KONTROLÜ
   ========================================================================== */

function useUiTexts() {
  const [uiLang, setUiLang] = useState(() =>
    (localStorage.getItem("app_lang") || "tr")
      .slice(0, 2)
      .toLowerCase(),
  );

  useEffect(() => {
    const sync = () => {
      const nextLang = (localStorage.getItem("app_lang") || "tr")
        .slice(0, 2)
        .toLowerCase();

      setUiLang(ALLOWED.includes(nextLang) ? nextLang : "tr");
    };

    window.addEventListener("app_lang_change", sync);
    window.addEventListener("storage", sync);
    window.addEventListener("focus", sync);

    return () => {
      window.removeEventListener("app_lang_change", sync);
      window.removeEventListener("storage", sync);
      window.removeEventListener("focus", sync);
    };
  }, []);

  return useMemo(
    () => T[ALLOWED.includes(uiLang) ? uiLang : "tr"],
    [uiLang],
  );
}

/* ==========================================================================
   KAYIT YARDIMCILARI
   ========================================================================== */

function detectPreferredLang(record = {}) {
  const candidate = String(record.dil || "").toLowerCase();

  if (ALLOWED.includes(candidate)) {
    return candidate;
  }

  for (const code of ALLOWED) {
    const value = record[`baslik_${code}`];

    if (
      value &&
      value !== PLACEHOLDER
    ) {
      return code;
    }
  }

  return "tr";
}

function pickLocalized(record, prefix) {
  if (!record) return "";

  const order = [record.dil, "tr", "en", "de"].filter(Boolean);

  for (const code of order) {
    const value = record[`${prefix}_${code}`];

    if (
      value &&
      value.trim() &&
      value.trim() !== PLACEHOLDER
    ) {
      return value;
    }
  }

  return "";
}

function pickRecordTitle(record) {
  return pickLocalized(record, "baslik") || "—";
}

function pickRecordDescription(record) {
  return pickLocalized(record, "aciklama");
}

function shortenLink(url) {
  if (!url) return "—";

  try {
    const parsed = new URL(url);
    return `${parsed.hostname}/…`;
  } catch {
    return url.length > 26
      ? `${url.slice(0, 26)}…`
      : url;
  }
}

/* ==========================================================================
   ÜST İŞLEM BUTONLARI
   ========================================================================== */

const ListActions = () => {
  const TT = useUiTexts();

  return (
    <TopToolbar
      sx={{
        width: "100%",

        px: {
          xs: 1.5,
          sm: 0,
        },

        py: 1.25,
        gap: 1,
        justifyContent: "flex-end",
        flexWrap: "wrap",
      }}
    >
      <CreateButton
        icon={<AddCircleIcon />}
        label={TT.newBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.5,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 850,
          bgcolor: `${COLORS.orange} !important`,
          color: `${COLORS.navy} !important`,
          boxShadow: "0 8px 20px rgba(245,158,11,.22)",

          "&:hover": {
            bgcolor: "#fbbf24 !important",
          },
        }}
      />

      <ExportButton
        icon={<FileDownloadIcon />}
        label={TT.exportBtn}
        sx={{
          minHeight: 42,

          px: {
            xs: 1.5,
            sm: 2.25,
          },

          borderRadius: 2.5,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.blue} !important`,
          color: "#fff !important",
          boxShadow: "0 8px 20px rgba(37,99,235,.18)",

          "&:hover": {
            bgcolor: "#1d4ed8 !important",
          },
        }}
      />
    </TopToolbar>
  );
};

/* ==========================================================================
   SİLME BUTONU
   DELETE /faaliyetler/:id KORUNDU
   ========================================================================== */

const CustomDeleteButton = ({
  record: recordProp,
  compact = false,
}) => {
  const TT = useUiTexts();
  const refresh = useRefresh();

  const contextRecord = useRecordContext();
  const record = recordProp || contextRecord;

  if (!record) return null;

  const remove = async (event) => {
    event?.stopPropagation?.();

    if (!window.confirm(TT.confirmDelete)) {
      return;
    }

    const token = localStorage.getItem("token");

    await fetch(
      `${API_BASE}/faaliyetler/${record.id}`,

      {
        method: "DELETE",

        headers: {
          Authorization: `Bearer ${token}`,
        },
      },
    );

    refresh();
  };

  if (compact) {
    return (
      <Tooltip title={TT.delete}>
        <IconButton
          color="error"
          onClick={remove}
          sx={{
            bgcolor: "#fff1f2",

            "&:hover": {
              bgcolor: "#ffe4e6",
            },
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Button
      onClick={remove}
      startIcon={<DeleteIcon />}
      color="error"
      variant="contained"
      size="small"
      sx={{
        minHeight: 38,
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 800,
        boxShadow: "none",
      }}
    >
      {TT.delete}
    </Button>
  );
};

/* ==========================================================================
   DÜZENLE / SİL BUTONLARI
   ========================================================================== */

const ActionButtons = ({
  compact = false,
  record: recordProp,
}) => {
  const TT = useUiTexts();

  const contextRecord = useRecordContext();
  const record = recordProp || contextRecord;

  if (!record) return null;

  return (
    <Stack
      direction="row"
      spacing={0.75}
      alignItems="center"
    >
      <EditButton
        record={record}
        icon={<EditIcon />}
        label={compact ? "" : TT.edit}
        onClick={(event) => event.stopPropagation()}
        sx={{
          minWidth: compact ? 42 : "auto",
          minHeight: 38,
          px: compact ? 0.75 : 1.5,
          borderRadius: 2,
          textTransform: "none",
          fontWeight: 800,
          bgcolor: `${COLORS.orangeSoft} !important`,
          color: "#b45309 !important",

          "& .MuiButton-startIcon": {
            mr: compact ? 0 : 1,
          },

          "&:hover": {
            bgcolor: "#ffedd5 !important",
          },
        }}
      />

      <CustomDeleteButton
        record={record}
        compact={compact}
      />
    </Stack>
  );
};

/* ==========================================================================
   TABLO ALANLARI
   ========================================================================== */

const ActivityImageField = () => {
  const record = useRecordContext();

  if (!record) return null;

  return (
    <Box
      sx={{
        width: 68,
        height: 68,
        borderRadius: 2.5,
        overflow: "hidden",
        border: `1px solid ${COLORS.border}`,
        bgcolor: "#f1f5f9",
        display: "grid",
        placeItems: "center",
      }}
    >
      {record.resim ? (
        <Box
          component="img"
          src={record.resim}
          alt={pickRecordTitle(record)}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <ImageIcon
          sx={{
            color: "#94a3b8",
          }}
        />
      )}
    </Box>
  );
};

const BaslikField = () => {
  const record = useRecordContext();

  if (!record) return null;

  const title = pickRecordTitle(record);
  const description = pickRecordDescription(record);

  return (
    <Box
      title={title}
      sx={{
        minWidth: 0,
      }}
    >
      <Typography
        sx={{
          fontWeight: 900,
          color: COLORS.text,
          lineHeight: 1.35,
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.4,
            color: COLORS.textSoft,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 440,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const DilField = ({ names }) => {
  const record = useRecordContext();

  if (!record) return null;

  const code = detectPreferredLang(record);

  return (
    <Chip
      size="small"
      icon={<LanguageIcon />}
      label={names[code] || "—"}
      sx={{
        fontWeight: 800,
        bgcolor: COLORS.blueSoft,

        "& .MuiChip-icon": {
          color: COLORS.blue,
        },
      }}
    />
  );
};

const StatusField = () => {
  const record = useRecordContext();
  const TT = useUiTexts();

  if (!record) return null;

  return (
    <Chip
      size="small"
      icon={
        record.aktif
          ? <CheckCircleIcon />
          : undefined
      }
      label={
        record.aktif
          ? TT.active
          : TT.passive
      }
      sx={{
        fontWeight: 850,

        color: record.aktif
          ? COLORS.greenDark
          : COLORS.textSoft,

        bgcolor: record.aktif
          ? COLORS.greenSoft
          : "#f1f5f9",

        "& .MuiChip-icon": {
          color: COLORS.green,
        },
      }}
    />
  );
};

function SocialLinkField({
  source,
  icon,
  color,
}) {
  const record = useRecordContext();

  if (!record) return null;

  const url = record[source] || "";

  if (!url) {
    return (
      <Typography color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={0.65}
      alignItems="center"
      minWidth={0}
    >
      <Box
        sx={{
          color,
          display: "grid",
          placeItems: "center",
        }}
      >
        {icon}
      </Box>

      <Typography
        variant="body2"
        title={url}
        sx={{
          fontWeight: 750,
          color: COLORS.text,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {shortenLink(url)}
      </Typography>
    </Stack>
  );
}

/* ==========================================================================
   BOŞ LİSTE
   ========================================================================== */

const EmptyState = () => {
  const TT = useUiTexts();

  return (
    <Box
      sx={{
        minHeight: "55vh",
        display: "grid",
        placeItems: "center",
        p: 2,
        bgcolor: COLORS.page,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 680,

          p: {
            xs: 3,
            sm: 5,
          },

          borderRadius: 4,
          borderStyle: "dashed",
          borderColor: "#cbd5e1",
          textAlign: "center",
          boxShadow: "0 16px 40px rgba(15,23,42,.05)",
        }}
      >
        <Box
          sx={{
            width: 82,
            height: 82,
            borderRadius: 3,
            bgcolor: COLORS.greenSoft,
            color: COLORS.greenDark,
            display: "grid",
            placeItems: "center",
            mx: "auto",
            mb: 2,
          }}
        >
          <AssignmentTurnedInIcon
            sx={{
              fontSize: 42,
            }}
          />
        </Box>

        <Typography
          variant="h5"
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {TT.emptyTitle}
        </Typography>

        <Typography
          sx={{
            mt: 1.25,
            color: COLORS.textSoft,
            lineHeight: 1.7,
          }}
        >
          {TT.emptyDesc}
        </Typography>

        <CreateButton
          label={TT.emptyBtn}
          icon={<AddCircleIcon />}
          sx={{
            mt: 2.5,
            minHeight: 46,
            px: 3,
            borderRadius: 2.5,
            textTransform: "none",
            fontWeight: 850,
            bgcolor: `${COLORS.orange} !important`,
            color: `${COLORS.navy} !important`,
          }}
        />
      </Paper>
    </Box>
  );
};

/* ==========================================================================
   LİSTE BAŞLIĞI
   ========================================================================== */

function ListHeader() {
  const TT = useUiTexts();
  const { total, data } = useListContext();

  const count = total ?? data?.length ?? 0;

  return (
    <Box
      sx={{
        p: {
          xs: 2,
          sm: 3,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #047857 130%)",

        borderBottom: `4px solid ${COLORS.orange}`,
        position: "relative",
        overflow: "hidden",

        "&::after": {
          content: '""',
          position: "absolute",
          width: 220,
          height: 220,
          borderRadius: "50%",
          right: -100,
          top: -150,
          bgcolor: "rgba(255,255,255,.1)",
        },
      }}
    >
      <Stack
        direction={{
          xs: "column",
          sm: "row",
        }}
        alignItems={{
          xs: "flex-start",
          sm: "center",
        }}
        justifyContent="space-between"
        spacing={2}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.75}
          alignItems="center"
        >
          <Box
            sx={{
              width: {
                xs: 48,
                sm: 56,
              },

              height: {
                xs: 48,
                sm: 56,
              },

              borderRadius: 3,
              display: "grid",
              placeItems: "center",
              color: "#fbbf24",
              bgcolor: "rgba(255,255,255,.11)",
              border: "1px solid rgba(255,255,255,.18)",
              flexShrink: 0,
            }}
          >
            <AssignmentTurnedInIcon fontSize="large" />
          </Box>

          <Box>
            <Typography
              component="h1"
              sx={{
                fontSize: {
                  xs: "1.55rem",
                  sm: "2rem",
                },

                fontWeight: 950,
              }}
            >
              {TT.listTitle}
            </Typography>

            <Typography
              variant="body2"
              sx={{
                mt: 0.4,
                color: "rgba(255,255,255,.76)",
              }}
            >
              {TT.listSubtitle}
            </Typography>
          </Box>
        </Stack>

        <Chip
          label={`${TT.totalLabel}: ${count}`}
          sx={{
            color: "#fff",
            fontWeight: 850,
            bgcolor: "rgba(255,255,255,.12)",
            border: "1px solid rgba(255,255,255,.2)",
          }}
        />
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   MOBİL FAALİYET KARTI
   ========================================================================== */

function MobileActivityCard({ record }) {
  const TT = useUiTexts();

  const title = pickRecordTitle(record);
  const description = pickRecordDescription(record);
  const language = detectPreferredLang(record);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3.5,
        borderColor: COLORS.border,
        boxShadow: "0 10px 28px rgba(15,23,42,.055)",
        overflow: "hidden",
        height: "100%",
      }}
    >
      <Box
        sx={{
          height: 5,

          background: record.aktif
            ? "linear-gradient(90deg, #10b981, #2563eb)"
            : "linear-gradient(90deg, #94a3b8, #cbd5e1)",
        }}
      />

      {record.resim ? (
        <Box
          component="img"
          src={record.resim}
          alt={title}
          sx={{
            width: "100%",
            height: 190,
            objectFit: "cover",
          }}
        />
      ) : (
        <Box
          sx={{
            height: 150,
            display: "grid",
            placeItems: "center",
            bgcolor: "#e2e8f0",
            color: "#94a3b8",
          }}
        >
          <ImageIcon
            sx={{
              fontSize: 48,
            }}
          />
        </Box>
      )}

      <CardContent
        sx={{
          p: 2,

          "&:last-child": {
            pb: 2,
          },
        }}
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          spacing={1}
        >
          <Box
            sx={{
              minWidth: 0,
            }}
          >
            <Typography
              sx={{
                fontWeight: 950,
                color: COLORS.text,
                fontSize: "1.08rem",
                overflowWrap: "anywhere",
              }}
            >
              {title}
            </Typography>

            {description && (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.55,
                  color: COLORS.textSoft,
                  lineHeight: 1.6,
                  display: "-webkit-box",
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {description}
              </Typography>
            )}
          </Box>

          <ActionButtons
            compact
            record={record}
          />
        </Stack>

        <Divider
          sx={{
            my: 1.5,
            borderColor: COLORS.border,
          }}
        />

        <Stack
          direction="row"
          spacing={0.7}
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            icon={<LanguageIcon />}
            label={TT.langNames[language]}
            sx={{
              fontWeight: 800,
              bgcolor: COLORS.blueSoft,

              "& .MuiChip-icon": {
                color: COLORS.blue,
              },
            }}
          />

          <Chip
            size="small"
            label={
              record.aktif
                ? TT.active
                : TT.passive
            }
            sx={{
              fontWeight: 850,

              color: record.aktif
                ? COLORS.greenDark
                : COLORS.textSoft,

              bgcolor: record.aktif
                ? COLORS.greenSoft
                : "#f1f5f9",
            }}
          />
        </Stack>

        {(record.youtube || record.instagram) && (
          <Stack
            spacing={1}
            sx={{
              mt: 1.5,
            }}
          >
            {record.youtube && (
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
              >
                <YouTubeIcon
                  sx={{
                    color: "#ef4444",
                  }}
                />

                <Typography
                  variant="caption"
                  title={record.youtube}
                  sx={{
                    color: COLORS.textSoft,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortenLink(record.youtube)}
                </Typography>
              </Stack>
            )}

            {record.instagram && (
              <Stack
                direction="row"
                spacing={0.8}
                alignItems="center"
              >
                <InstagramIcon
                  sx={{
                    color: "#c026d3",
                  }}
                />

                <Typography
                  variant="caption"
                  title={record.instagram}
                  sx={{
                    color: COLORS.textSoft,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {shortenLink(record.instagram)}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

/* ==========================================================================
   MOBİL VE MASAÜSTÜ LİSTE
   ========================================================================== */

function ResponsiveActivityList() {
  const TT = useUiTexts();
  const { data = [] } = useListContext();

  return (
    <>
      {/* MOBİL VE TABLET */}

      <Box
        sx={{
          display: {
            xs: "block",
            lg: "none",
          },

          p: 1.5,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr)",
              sm: "repeat(2, minmax(0, 1fr))",
            },
            gap: 1.5,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          {data.map((record) => (
            <Box
              key={record.id}
              sx={{
                display: "flex",
                width: "100%",
                maxWidth: "100%",
                minWidth: 0,
              }}
            >
              <MobileActivityCard record={record} />
            </Box>
          ))}
        </Box>
      </Box>

      {/* MASAÜSTÜ TABLO */}

      <Box
        sx={{
          display: {
            xs: "none",
            lg: "block",
          },

          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",

          "&::-webkit-scrollbar": {
            height: 8,
          },

          "&::-webkit-scrollbar-thumb": {
            borderRadius: 8,
            bgcolor: "#cbd5e1",
          },
        }}
      >
        <Datagrid
          rowClick="edit"
          bulkActionButtons={false}
          sx={{
            width: "100%",
            minWidth: 860,
            tableLayout: "fixed",

            "& .RaDatagrid-headerCell": {
              py: 1.6,
              bgcolor: "#f8fafc",
              color: COLORS.textSoft,
              fontWeight: 900,
              fontSize: ".76rem",
              textTransform: "uppercase",
              letterSpacing: 0.4,
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .RaDatagrid-row": {
              transition: "background-color .2s ease",

              "&:hover": {
                bgcolor: "#f8fafc",
              },
            },

            "& .MuiTableCell-root": {
              py: 1.2,
              px: 1.35,
              verticalAlign: "middle",
              borderBottom: `1px solid ${COLORS.border}`,
            },

            "& .column-resim": {
              width: 76,
              minWidth: 76,
            },

            "& .column-baslik": {
              width: "auto",
            },

            "& .column-youtube": {
              width: "17%",
            },

            "& .column-instagram": {
              width: "17%",
            },

            "& .column-aktif": {
              width: 86,
              minWidth: 86,
            },

            "& .column-lang": {
              width: 104,
              minWidth: 104,
            },

            "& .column-actions": {
              position: "sticky",
              right: 0,
              zIndex: 3,
              width: 174,
              minWidth: 174,
              bgcolor: "#fff",
              boxShadow: "-8px 0 14px rgba(15,23,42,.06)",
            },

            "& .RaDatagrid-headerCell.column-actions": {
              zIndex: 5,
              bgcolor: "#f8fafc",
            },

            "& .column-actions .MuiStack-root": {
              width: "100%",
              justifyContent: "flex-end",
              gap: 0.5,
            },

            "& .column-actions .MuiButton-root": {
              minWidth: 0,
              px: 1.15,
            },

            "@media (max-width: 1599px)": {
              minWidth: 760,

              "& .column-instagram": {
                display: "none",
              },

              "& .column-youtube": {
                width: "22%",
              },
            },

            "@media (max-width: 1350px)": {
              minWidth: 680,

              "& .column-youtube": {
                display: "none",
              },

              "& .column-baslik": {
                width: "auto",
              },
            },
          }}
        >
          <FunctionField
            label={TT.image}
            className="column-resim"
            render={() => <ActivityImageField />}
          />

          <FunctionField
            label={TT.title}
            className="column-baslik"
            render={() => <BaslikField />}
          />

          <FunctionField
            label={TT.youtube}
            className="column-youtube"
            render={() => (
              <SocialLinkField
                source="youtube"
                icon={
                  <YouTubeIcon fontSize="small" />
                }
                color="#ef4444"
              />
            )}
          />

          <FunctionField
            label={TT.instagram}
            className="column-instagram"
            render={() => (
              <SocialLinkField
                source="instagram"
                icon={
                  <InstagramIcon fontSize="small" />
                }
                color="#c026d3"
              />
            )}
          />

          <FunctionField
            label={TT.status}
            className="column-aktif"
            render={() => <StatusField />}
          />

          <FunctionField
            label={TT.contentLang}
            className="column-lang"
            render={() => (
              <DilField names={TT.langNames} />
            )}
          />

          <FunctionField
            label={TT.operations}
            className="column-actions"
            render={() => <ActionButtons />}
          />
        </Datagrid>
      </Box>
    </>
  );
}

/* ==========================================================================
   LİSTE SAYFASI
   ========================================================================== */

export const FaaliyetlerPage = (props) => {
  const TT = useUiTexts();

  return (
    <>
      <PageGlobalStyles />

      <List
        {...props}
        resource="faaliyetler"
        title={TT.listTitle}
        actions={<ListActions />}
        empty={<EmptyState />}
        sort={{
          field: "id",
          order: "DESC",
        }}
        sx={{
          m: 0,
          p: 0,
          width: "100%",
          maxWidth: "100%",
          bgcolor: COLORS.page,

          "& .RaList-main": {
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          },

          "& .RaList-content": {
            m: 0,
            p: 0,
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
            overflow: "hidden",

            borderRadius: {
              xs: 0,
              md: 3.5,
            },

            border: {
              md: `1px solid ${COLORS.border}`,
            },

            boxShadow: {
              md: "0 18px 48px rgba(15,23,42,.07)",
            },
          },

          "& .MuiCard-root": {
            boxShadow: "none",
          },

          "& .MuiContainer-root": {
            maxWidth: "100% !important",
            px: "0 !important",
          },
        }}
      >
        <Box>
          <ListHeader />
          <ResponsiveActivityList />
        </Box>
      </List>
    </>
  );
};

/* ==========================================================================
   GLOBAL TASARIM
   ========================================================================== */

function PageGlobalStyles() {
  return (
    <GlobalStyles
      styles={{
        html: {
          overflowX: "hidden",
        },

        body: {
          overflowX: "hidden",
          background: COLORS.page,
        },

        "*": {
          boxSizing: "border-box",
        },

        ".MuiSnackbarContent-message": {
          fontSize: "1rem",
          fontWeight: 800,
        },

        ".RaNotification-content": {
          fontSize: "1rem",
          fontWeight: 800,
        },
      }}
    />
  );
}

/* ==========================================================================
   FORM SAYFASI BAŞLIĞI
   ========================================================================== */

function FormPageHeader({
  title,
  subtitle,
  TT,
  onBack,
}) {
  return (
    <Box
      sx={{
        p: {
          xs: 2.25,
          sm: 3.5,
        },

        color: "#fff",

        background:
          "linear-gradient(125deg, #071a33 0%, #123b6d 58%, #047857 130%)",

        borderBottom: `4px solid ${COLORS.orange}`,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
      >
        <Tooltip title={TT.cancel}>
          <IconButton
            onClick={onBack}
            sx={{
              color: "#fff",
              bgcolor: "rgba(255,255,255,.12)",
              border: "1px solid rgba(255,255,255,.18)",

              "&:hover": {
                bgcolor: "rgba(255,255,255,.2)",
              },
            }}
          >
            <ArrowBackIcon />
          </IconButton>
        </Tooltip>

        <Box>
          <Typography
            component="h1"
            sx={{
              fontSize: {
                xs: "1.45rem",
                sm: "2rem",
              },

              fontWeight: 950,
            }}
          >
            {title}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mt: 0.35,
              color: "rgba(255,255,255,.76)",
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* ==========================================================================
   FORM BÖLÜM BAŞLIĞI
   ========================================================================== */

function FormSectionTitle({
  icon,
  title,
  subtitle,
}) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        mb: 2.25,
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2.5,
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.greenSoft,
          color: COLORS.greenDark,
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>

      <Box>
        <Typography
          sx={{
            fontWeight: 950,
            color: COLORS.text,
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="caption"
            sx={{
              color: COLORS.textSoft,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/* ==========================================================================
   ORTAK FAALİYET FORMU
   ========================================================================== */

function ActivityForm({
  form,
  setForm,
  onSubmit,
  saving,
  TT,
  onCancel,
}) {
  const titleKey = `baslik_${form.dil}`;
  const descriptionKey = `aciklama_${form.dil}`;

  const setValue = (name, value) => {
    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      sx={{
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            lg: "minmax(0, 7fr) minmax(0, 5fr)",
          },
          gap: 2.5,
          alignItems: "stretch",
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        {/* FAALİYET BİLGİLERİ */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<AssignmentTurnedInIcon />}
              title={TT.formInfo}
              subtitle={TT.contentLang}
            />

            <Stack spacing={2.2}>
              <MuiTextField
                select
                fullWidth
                label={TT.contentLang}
                name="dil"
                value={form.dil}
                onChange={(event) =>
                  setValue(
                    "dil",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              >
                {ALLOWED.map((code) => (
                  <MenuItem
                    key={code}
                    value={code}
                  >
                    {TT.langNames[code]}
                  </MenuItem>
                ))}
              </MuiTextField>

              <MuiTextField
                fullWidth
                label={TT.title}
                name={titleKey}
                value={form[titleKey] || ""}
                onChange={(event) =>
                  setValue(
                    titleKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                multiline
                minRows={7}
                maxRows={14}
                label={TT.description}
                name={descriptionKey}
                value={form[descriptionKey] || ""}
                onChange={(event) =>
                  setValue(
                    descriptionKey,
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                label={TT.youtube}
                name="youtube"
                value={form.youtube || ""}
                placeholder="https://youtube.com/..."
                onChange={(event) =>
                  setValue(
                    "youtube",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />

              <MuiTextField
                fullWidth
                label={TT.instagram}
                name="instagram"
                value={form.instagram || ""}
                placeholder="https://instagram.com/..."
                onChange={(event) =>
                  setValue(
                    "instagram",
                    event.target.value,
                  )
                }
                sx={fieldSx}
              />
            </Stack>
          </Paper>
        </Box>

        {/* GÖRSEL VE YAYIN AYARLARI */}

        <Box
          sx={{
            display: "flex",
            width: "100%",
            maxWidth: "100%",
            minWidth: 0,
          }}
        >
          <Paper
            variant="outlined"
            sx={{
              width: "100%",

              p: {
                xs: 2,
                sm: 3,
              },

              borderRadius: 4,
              borderColor: COLORS.border,
              boxShadow: "0 12px 34px rgba(15,23,42,.05)",
            }}
          >
            <FormSectionTitle
              icon={<ImageIcon />}
              title={TT.mediaInfo}
              subtitle={
                form.aktif
                  ? TT.active
                  : TT.passive
              }
            />

            <Stack spacing={2.2}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,
                  bgcolor: "#f8fafc",
                  overflow: "hidden",

                  "& button": {
                    maxWidth: "100%",
                  },
                }}
              >
                <ResimUpload
                  label={TT.uploadImage}
                  defaultPreview={form.resim}
                  onUpload={(url) =>
                    setValue(
                      "resim",
                      url,
                    )
                  }
                />
              </Paper>

              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 3,
                  borderColor: COLORS.border,

                  bgcolor: form.aktif
                    ? COLORS.greenSoft
                    : "#f8fafc",
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.aktif}
                      onChange={(event) =>
                        setValue(
                          "aktif",
                          event.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    form.aktif
                      ? TT.active
                      : TT.passive
                  }
                  sx={{
                    m: 0,
                    width: "100%",

                    "& .MuiFormControlLabel-label": {
                      fontWeight: 850,

                      color: form.aktif
                        ? COLORS.greenDark
                        : COLORS.textSoft,
                    },
                  }}
                />
              </Paper>

              <Divider
                sx={{
                  borderColor: COLORS.border,
                }}
              />

              <Box>
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: COLORS.text,
                  }}
                >
                  {TT.preview}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: COLORS.textSoft,
                  }}
                >
                  {TT.previewInfo}
                </Typography>
              </Box>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 3.5,
                  borderColor: COLORS.border,
                  overflow: "hidden",
                  boxShadow: "0 10px 28px rgba(15,23,42,.06)",
                }}
              >
                {form.resim ? (
                  <Box
                    component="img"
                    src={form.resim}
                    alt={
                      form[titleKey] ||
                      TT.title
                    }
                    sx={{
                      width: "100%",
                      height: 210,
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 190,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "#e2e8f0",
                      color: "#94a3b8",
                    }}
                  >
                    <ImageIcon
                      sx={{
                        fontSize: 54,
                      }}
                    />
                  </Box>
                )}

                <CardContent>
                  <Typography
                    sx={{
                      fontWeight: 950,
                      color: COLORS.text,
                    }}
                  >
                    {form[titleKey] || TT.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0.6,
                      color: COLORS.textSoft,
                      lineHeight: 1.6,
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {form[descriptionKey] || TT.description}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{
                      mt: 1.5,
                    }}
                  >
                    <Chip
                      size="small"
                      icon={<LanguageIcon />}
                      label={TT.langNames[form.dil]}
                      sx={{
                        fontWeight: 800,
                        bgcolor: COLORS.blueSoft,
                      }}
                    />

                    {form.youtube && (
                      <Chip
                        size="small"
                        icon={<YouTubeIcon />}
                        label="YouTube"
                        sx={{
                          fontWeight: 800,
                          color: "#b91c1c",
                          bgcolor: "#fef2f2",

                          "& .MuiChip-icon": {
                            color: "#ef4444",
                          },
                        }}
                      />
                    )}

                    {form.instagram && (
                      <Chip
                        size="small"
                        icon={<InstagramIcon />}
                        label="Instagram"
                        sx={{
                          fontWeight: 800,
                          color: "#a21caf",
                          bgcolor: "#fdf4ff",

                          "& .MuiChip-icon": {
                            color: "#c026d3",
                          },
                        }}
                      />
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </Paper>
        </Box>
      </Box>

      {/* FORM BUTONLARI */}

      <Paper
        elevation={0}
        sx={{
          mt: 2.5,
          p: 1.5,
          borderRadius: 3,
          border: `1px solid ${COLORS.border}`,
          bgcolor: "#fff",
        }}
      >
        <Stack
          direction={{
            xs: "column-reverse",
            sm: "row",
          }}
          justifyContent="flex-end"
          spacing={1.25}
        >
          <Button
            type="button"
            onClick={onCancel}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              minHeight: 48,
              px: 3,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 850,
              borderColor: COLORS.border,
              color: COLORS.text,
            }}
          >
            {TT.cancel}
          </Button>

          <Button
            type="submit"
            disabled={saving}
            startIcon={
              saving ? (
                <CircularProgress
                  size={18}
                  color="inherit"
                />
              ) : (
                <SaveIcon />
              )
            }
            variant="contained"
            sx={{
              minHeight: 48,
              px: 3.5,
              borderRadius: 2.5,
              textTransform: "none",
              fontWeight: 900,
              bgcolor: COLORS.navy,
              boxShadow: "0 10px 25px rgba(11,31,58,.2)",

              "&:hover": {
                bgcolor: COLORS.navySoft,
              },
            }}
          >
            {saving
              ? TT.saving
              : TT.save}
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ==========================================================================
   FORM SAYFASI DIŞ YAPISI
   ========================================================================== */

function FormPageShell({
  title,
  subtitle,
  TT,
  onBack,
  children,
}) {
  return (
    <>
      <PageGlobalStyles />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: COLORS.page,
          pb: 5,
        }}
      >
        <FormPageHeader
          title={title}
          subtitle={subtitle}
          TT={TT}
          onBack={onBack}
        />

        <Container
          maxWidth="xl"
          sx={{
            width: "100%",
            minWidth: 0,
            boxSizing: "border-box",

            mt: {
              xs: 2,
              md: 3,
            },

            px: {
              xs: 1.5,
              sm: 3,
            },
          }}
        >
          {children}
        </Container>
      </Box>
    </>
  );
}

/* ==========================================================================
   FAALİYET DÜZENLEME
   ========================================================================== */

function FaaliyetEditView() {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const {
    record,
    isLoading,
  } = useEditContext();

  const [
    update,
    {
      isLoading: saving,
    },
  ] = useUpdate();

  const [form, setForm] = useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    resim: "",
    youtube: "",
    instagram: "",
    aktif: true,
    dil: "tr",
  });

  useEffect(() => {
    if (!isLoading && record) {
      setForm({
        baslik_tr: record.baslik_tr || "",
        baslik_en: record.baslik_en || "",
        baslik_de: record.baslik_de || "",

        aciklama_tr: record.aciklama_tr || "",
        aciklama_en: record.aciklama_en || "",
        aciklama_de: record.aciklama_de || "",

        resim: record.resim || "",
        youtube: record.youtube || "",
        instagram: record.instagram || "",
        aktif: !!record.aktif,
        dil: detectPreferredLang(record),
      });
    }
  }, [isLoading, record]);

  const submit = async (event) => {
    event.preventDefault();

    if (!record) return;

    const merged = {
      ...record,
      ...form,
      aktif: !!form.aktif,
      dil: form.dil,
    };

    update(
      "faaliyetler",

      {
        id: record.id,
        data: merged,
      },

      {
        onSuccess: () => {
          notify(
            TT.updatedOk,
            {
              type: "success",
            },
          );

          redirect("/faaliyetler");
        },

        onError: () =>
          notify(
            TT.updateError,
            {
              type: "error",
            },
          ),
      },
    );
  };

  if (
    isLoading ||
    !record
  ) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "grid",
          placeItems: "center",
          bgcolor: COLORS.page,
        }}
      >
        <Stack
          alignItems="center"
          spacing={1.5}
        >
          <CircularProgress />

          <Typography color="text.secondary">
            {TT.loading}
          </Typography>
        </Stack>
      </Box>
    );
  }

  return (
    <FormPageShell
      title={TT.editTitle}
      subtitle={TT.editSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/faaliyetler")
      }
    >
      <ActivityForm
        form={form}
        setForm={setForm}
        onSubmit={submit}
        saving={saving}
        TT={TT}
        onCancel={() =>
          redirect("/faaliyetler")
        }
      />
    </FormPageShell>
  );
}

export const FaaliyetEdit = (props) => (
  <EditBase
    resource="faaliyetler"
    {...props}
  >
    <FaaliyetEditView />
  </EditBase>
);

/* ==========================================================================
   YENİ FAALİYET
   POST /faaliyetler VE PAYLOAD KORUNDU
   ========================================================================== */

export const FaaliyetCreate = () => {
  const TT = useUiTexts();
  const notify = useNotify();
  const redirect = useRedirect();

  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    baslik_tr: "",
    baslik_en: "",
    baslik_de: "",

    aciklama_tr: "",
    aciklama_en: "",
    aciklama_de: "",

    resim: "",
    youtube: "",
    instagram: "",
    aktif: true,
    dil: "tr",
  });

  const submit = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      const token = localStorage.getItem("token");

      const body = {
        ...form,
        aktif: !!form.aktif,
      };

      const response = await fetch(
        `${API_BASE}/faaliyetler`,

        {
          method: "POST",

          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },

          body: JSON.stringify(body),
        },
      );

      if (response.ok) {
        notify(
          TT.createdOk,
          {
            type: "success",
          },
        );

        redirect("/faaliyetler");
      } else {
        notify(
          TT.createError,
          {
            type: "error",
          },
        );
      }
    } catch (error) {
      console.error(
        "Faaliyet kayıt hatası:",
        error,
      );

      notify(
        TT.createError,
        {
          type: "error",
        },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageShell
      title={TT.createTitle}
      subtitle={TT.createSubtitle}
      TT={TT}
      onBack={() =>
        redirect("/faaliyetler")
      }
    >
      <ActivityForm
        form={form}
        setForm={setForm}
        onSubmit={submit}
        saving={saving}
        TT={TT}
        onCancel={() =>
          redirect("/faaliyetler")
        }
      />
    </FormPageShell>
  );
};
